import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { AsyncBoundary, Drawer, Icon, useToast } from '@kollez/design-system';
import { apiGet } from '@/lib/api';
import { formatIndianDate } from '@kollez/shared';
import { initialTableParams, toQueryString, type Paginated, type TableQueryParams } from '@/lib/tableQuery';
import { Pagination, SortHeader, TableSearch, ResetButton } from '@/components/DataTable';
import { useMarkInvoicePaid } from '@/lib/queries';

interface InvoiceRow {
  _id: string;
  invoiceNumber: string;
  tenant: { _id: string; name?: string; slug?: string } | string | null;
  amount?: number;
  totalAmount?: number;
  status: string;
  issuedAt?: string;
  dueAt?: string;
  paidAt?: string;
  kind?: string;
  po?: string;
}

const STATUS_OPTIONS = [
  ['', 'Any status'],
  ['Draft', 'Draft'],
  ['Sent', 'Sent'],
  ['Overdue', 'Overdue'],
  ['Paid', 'Paid'],
  ['Cancelled', 'Cancelled'],
] as const;

const initial: TableQueryParams = initialTableParams({ sort: 'issuedAt', order: 'desc' });

function tenantName(t: InvoiceRow['tenant']): string {
  if (!t) return '—';
  if (typeof t === 'string') return t;
  return t.name ?? '—';
}
function amount(r: InvoiceRow): number {
  return r.totalAmount ?? r.amount ?? 0;
}

function MarkPaidForm({ invoiceId, invoiceNumber, amount, onDone }: {
  invoiceId: string; invoiceNumber: string; amount: number; onDone: () => void;
}) {
  const toast = useToast();
  const mark = useMarkInvoicePaid();
  const [utr, setUtr] = useState('');
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [amt, setAmt] = useState(String(amount || ''));
  const [note, setNote] = useState('');
  const disabled = utr.trim().length < 4 || mark.isPending;
  return (
    <div className="card card-pad grid gap-3 mt-3">
      <div className="eyebrow">Record NEFT / UTR payment</div>
      <label className="block">
        <span className="eyebrow">UTR / bank reference (min 4 chars)</span>
        <input className="input mt-1" value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. UTR2026091700045" />
      </label>
      <div className="grid gap-3 md:grid-cols-2">
        <label className="block">
          <span className="eyebrow">Paid on</span>
          <input className="input mt-1" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
        </label>
        <label className="block">
          <span className="eyebrow">Amount (₹, optional)</span>
          <input className="input mt-1" type="number" value={amt} onChange={(e) => setAmt(e.target.value)} />
        </label>
      </div>
      <label className="block">
        <span className="eyebrow">Note (optional)</span>
        <input className="input mt-1" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Partial payment, holdback, etc." />
      </label>
      <div className="flex gap-2 justify-end">
        <button
          className="btn btn-primary"
          disabled={disabled}
          onClick={() => {
            mark.mutate(
              {
                invoiceId,
                utr: utr.trim(),
                paidAt: new Date(paidAt).toISOString(),
                amountInr: amt ? Number(amt) : undefined,
                note: note.trim() || undefined,
              },
              {
                onSuccess: () => {
                  toast.show({ message: `${invoiceNumber} marked paid.` });
                  onDone();
                },
                onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
              },
            );
          }}
        >
          {mark.isPending ? 'Recording…' : 'Mark paid'}
        </button>
      </div>
    </div>
  );
}

export function PlatformInvoicesPage() {
  const toast = useToast();
  const [params, setParams] = useState<TableQueryParams>(initial);
  const [openId, setOpenId] = useState<string | null>(null);
  const [qDraft, setQDraft] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setParams((p) => ({ ...p, q: qDraft || undefined, page: 1 })), 300);
    return () => clearTimeout(t);
  }, [qDraft]);

  const query = useQuery({
    queryKey: ['platform', 'invoices', params],
    queryFn: () => apiGet<Paginated<InvoiceRow>>(`/platform/invoices?${toQueryString(params)}`),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.rows ?? [];
  const opened = rows.find((r) => r._id === openId) ?? null;

  const kpis = useMemo(() => ({
    billed: rows.reduce((s, r) => s + amount(r), 0),
    collected: rows.filter((r) => r.status === 'Paid').reduce((s, r) => s + amount(r), 0),
    overdueAmt: rows.filter((r) => r.status === 'Overdue').reduce((s, r) => s + amount(r), 0),
    checking: rows.filter((r) => r.status === 'Sent').length,
  }), [rows]);

  const onSort = (field: string) => setParams((p) => ({
    ...p,
    sort: field,
    order: p.sort === field && p.order === 'asc' ? 'desc' : 'asc',
    page: 1,
  }));

  const reset = () => {
    setQDraft('');
    setParams(initial);
  };
  const filtersDirty = !!(params.q || params.status || params.sort !== initial.sort || params.order !== initial.order);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Invoices</h1>
          <p className="subhead">All tenants. Indian B2B terms: GST invoice against a purchase order, paid by NEFT/RTGS within 30 days.</p>
        </div>
        <div className="ph-actions">
          <button
            className="btn btn-outline"
            disabled
            title="Invoices bulk-export lands after /platform/audit/export.csv pattern is repeated here."
          >
            <Icon name="download" size={14} />Export · soon
          </button>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <div className="card kpi">
          <span className="kpi-label">Billed (this view)</span>
          <span className="kpi-value">₹{kpis.billed.toLocaleString('en-IN')}</span>
        </div>
        <div className="card kpi" data-tone="success">
          <span className="kpi-label">Collected</span>
          <span className="kpi-value">₹{kpis.collected.toLocaleString('en-IN')}</span>
        </div>
        <div className="card kpi" data-tone="danger">
          <span className="kpi-label">Overdue amount</span>
          <span className="kpi-value">₹{kpis.overdueAmt.toLocaleString('en-IN')}</span>
        </div>
        <div className="card kpi" data-tone="warning">
          <span className="kpi-label">Awaiting payment</span>
          <span className="kpi-value">{kpis.checking}</span>
        </div>
      </div>

      <div className="card">
        <div className="filterbar">
          <TableSearch value={qDraft} onChange={setQDraft} placeholder="Invoice # or PO" />
          <select className="select" value={String(params.status ?? '')} onChange={(e) => setParams((p) => ({ ...p, status: e.target.value || undefined, page: 1 }))}>
            {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <ResetButton onReset={reset} disabled={!filtersDirty} />
          <span className="fb-count">{query.data ? `${query.data.total.toLocaleString('en-IN')} total` : ''}</span>
        </div>

        <AsyncBoundary kind="table" inCard loading={query.isLoading} error={query.error} empty={!!(query.data && query.data.total === 0)} emptyTitle="No invoices">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <SortHeader label="Invoice #" field="invoiceNumber" currentSort={params.sort ?? null} currentOrder={params.order ?? 'desc'} onSort={onSort} />
                  <th>Tenant</th>
                  <th className="hide-mobile">Kind</th>
                  <SortHeader label="Amount" field="totalAmount" currentSort={params.sort ?? null} currentOrder={params.order ?? 'desc'} onSort={onSort} className="r" />
                  <SortHeader label="Issued" field="issuedAt" currentSort={params.sort ?? null} currentOrder={params.order ?? 'desc'} onSort={onSort} className="hide-mobile" />
                  <SortHeader label="Due" field="dueAt" currentSort={params.sort ?? null} currentOrder={params.order ?? 'desc'} onSort={onSort} className="hide-mobile" />
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id} onClick={() => setOpenId(r._id)} style={{ cursor: 'pointer' }}>
                    <td className="cell-title">{r.invoiceNumber}</td>
                    <td>{tenantName(r.tenant)}</td>
                    <td className="hide-mobile">{r.kind ?? '—'}</td>
                    <td className="r">₹{amount(r).toLocaleString('en-IN')}</td>
                    <td className="hide-mobile cell-sub">{r.issuedAt ? formatIndianDate(new Date(r.issuedAt)) : '—'}</td>
                    <td className="hide-mobile cell-sub">{r.dueAt ? formatIndianDate(new Date(r.dueAt)) : '—'}</td>
                    <td>
                      <span className={`badge ${
                        r.status === 'Paid' ? 'badge-success'
                          : r.status === 'Overdue' ? 'badge-danger'
                          : r.status === 'Sent' ? 'badge-warning'
                          : ''
                      }`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {query.data && (
            <Pagination
              page={query.data.page}
              totalPages={query.data.totalPages}
              total={query.data.total}
              limit={query.data.limit}
              onPage={(page) => setParams((p) => ({ ...p, page }))}
              onLimit={(limit) => setParams((p) => ({ ...p, limit, page: 1 }))}
            />
          )}
        </AsyncBoundary>
      </div>

      <Drawer open={!!opened} onClose={() => setOpenId(null)} title={opened?.invoiceNumber ?? ''} size="md">
        {opened && (
          <>
            <p className="eyebrow">{tenantName(opened.tenant)} · {opened.status}</p>
            <dl className="dl">
              <dt>Amount</dt><dd>₹{amount(opened).toLocaleString('en-IN')}</dd>
              <dt>Kind</dt><dd>{opened.kind ?? '—'}</dd>
              <dt>Issued</dt><dd>{opened.issuedAt ? formatIndianDate(new Date(opened.issuedAt)) : '—'}</dd>
              <dt>Due</dt><dd>{opened.dueAt ? formatIndianDate(new Date(opened.dueAt)) : '—'}</dd>
              {opened.paidAt && <><dt>Paid</dt><dd>{formatIndianDate(new Date(opened.paidAt))}</dd></>}
              {opened.po && <><dt>PO</dt><dd>{opened.po}</dd></>}
            </dl>
            {opened.status !== 'Paid' && <MarkPaidForm invoiceId={opened._id} invoiceNumber={opened.invoiceNumber} amount={amount(opened)} onDone={() => setOpenId(null)} />}
            <div className="drawer-foot">
              <button
                className="btn btn-ghost ml-auto"
                disabled
                title="PDF generation ships with the invoice-worker rollout (plan §11)."
              >
                <Icon name="download" size={14} />PDF · soon
              </button>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}
